import React, { useState, useEffect } from "react";
import {
    PieChart,
    Pie,
    Cell,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";
import { getData } from "../../../webService/webservice.js";
import "../../../styles/Auth/report.css";

const COLORS = ["#A25B65", "#E74C3C", "#9B59B6"];

const GraphReport = () => {
    const [activeMenu, setActiveMenu] = useState("Customer type chart");
    const [chartData, setChartData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const sessionToken = sessionStorage.getItem("Token");
    const fetchReport = async (type) => {
        try {
            let endpoint = "";
            let params = {};
            switch (type) {
                case "Customer type chart":
                    endpoint = "reports/reports/customertype";
                    break;

                case "Booking per Day":
                    endpoint = "reports/reports/bookingperday";
                    params.date = selectedDate;
                    break;

                case "Booking per Month":
                    endpoint = "reports/reports/bookingpermonth";
                    params.month = selectedMonth;
                    params.year = selectedYear;
                    break;

                case "Booking per Year":
                    endpoint = "reports/reports/bookingperyear";
                    params.year = selectedYear;
                    break;

                default:
                    return;
            }

            const response = await getData(endpoint, params, sessionToken);
            if (response.success) {
                let cleanedData = response.data;
                if (type === "Customer type chart") {
                    cleanedData = cleanedData
                        .map((item) => ({
                            name: item.name && item.name.trim() !== "" ? item.name : "Unknown",
                            value: item.value || 0,
                        }))
                        .filter((item) => ["Male", "Female", "Other"].includes(item.name));
                }
                else {
                    cleanedData = cleanedData.map((item) => ({
                        name: item.name || "Unknown",
                        value: item.value || 0,
                    }));
                }

                setChartData(cleanedData);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchReport(activeMenu);
    }, [activeMenu, selectedDate, selectedMonth, selectedYear]);

    return (
        <div className="charts-container">
            <div className="charts-sidebar">
                <h3>Charts</h3>
                <ul>
                    {["Customer type chart", "Booking per Day", "Booking per Month", "Booking per Year"].map((item) => (
                        <li
                            key={item}
                            className={activeMenu === item ? "active" : ""}
                            onClick={() => setActiveMenu(item)}
                        >
                            {item}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="charts-content">
                <h3>{activeMenu}</h3>

                {/* Filters */}
                {activeMenu === "Booking per Day" && (
                    <div className="filters">
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />
                    </div>
                )}

                {activeMenu === "Booking per Month" && (
                    <div className="filters">
                        <div style={{ marginBottom: "10px" }}>
                            <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </select>

                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                style={{ marginLeft: "10px" }}
                            >
                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {activeMenu === "Booking per Year" && (
                    <div className="filters">
                        <div style={{ marginBottom: "10px" }}>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                            >
                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <ResponsiveContainer width="100%" height={300}>
                    {activeMenu === "Customer type chart" ? (
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={3}
                                dataKey="value"
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                    ) : (
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                            <XAxis
                                dataKey="name"
                                label={{
                                    value:
                                        activeMenu === "Booking per Day"
                                            ? "Hour"
                                            : activeMenu === "Booking per Month"
                                                ? "Day"
                                                : "Month",
                                    position: "insideBottomRight",
                                    offset: -5,
                                }}
                            />
                            <YAxis
                                allowDecimals={false}
                                label={{ value: "Bookings", angle: -90, position: "insideLeft" }}
                            />
                            <Tooltip formatter={(value) => [`${value} Booking(s)`]} />
                            <Bar dataKey="value" barSize={40} radius={[5, 5, 0, 0]}>
                                {chartData.map((entry, index) => (
                                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    )}
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default GraphReport;
